import React, { useState, useMemo } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';

interface CompanyDocument {
  document_id: string;
  file_name_no_ext: string;
  current_date?: string;
  current_time?: string;
  username?: string;
  source_language?: string;
  destination_language?: string;
  assigned_user?: number | number[];
  assigned_username?: string;
  assigned_usernames?: string;
  checked_count?: string;
  unchecked_count?: string;
  is_completed?: boolean;
}

interface CompanyDocumentsProps {
  selectedCompany: { name: string; company_id?: number; id: string } | null;
  companyDocuments: CompanyDocument[] | null;
  isLoadingDocuments: boolean;
  documentsError: string | null;
  onBackToOptions: () => void;
  onRefreshDocuments: () => void;
  onGoToTranslate: () => void;
  onViewDocument: (documentId: string, documentName: string) => void;
  onDeleteDocument: (documentId: string) => void;
  onToggleCompletion: (documentId: string, currentStatus: boolean) => Promise<any>;
}

const CompanyDocuments: React.FC<CompanyDocumentsProps> = ({
  selectedCompany,
  companyDocuments,
  isLoadingDocuments,
  documentsError,
  onBackToOptions,
  onRefreshDocuments,
  onGoToTranslate,
  onViewDocument,
  onDeleteDocument,
  onToggleCompletion
}) => {
  const { canToggleDocumentCompletion, canDeleteDocuments } = useAuth();
  
  // Arama state'i
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Sıralama ve filtreleme state'leri
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [nameFilter, setNameFilter] = useState<string>('all');

  // Helper function to format date and time
  const formatDateTime = (dateStr: string, timeStr: string) => {
    if (!dateStr || !timeStr) return '';
    
    // dateStr format: YYYYMMDD (e.g., "20251008")
    // timeStr format: HHMM (e.g., "1751")
    
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    
    const hour = timeStr.substring(0, 2);
    const minute = timeStr.substring(2, 4);
    
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
    
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to get language name from code
  const getLanguageName = (languageCode: string): string => {
    const languageMap: { [key: string]: string } = {
      'tr': 'Türkçe',
      'en': 'İngilizce',
      'es': 'İspanyolca',
      'fr': 'Fransızca',
      'de': 'Almanca',
      'it': 'İtalyanca',
      'pt': 'Portekizce',
      'ru': 'Rusça',
      'ar': 'Arapça',
      'zh': 'Çince',
      'ja': 'Japonca',
      'ko': 'Korece'
    };
    return languageMap[languageCode] || languageCode.toUpperCase();
  };

  // Türkçe karakterleri normalize etme fonksiyonu
  const normalizeTurkish = (text: string): string => {
    return text
      .replace(/İ/g, 'i')
      .replace(/I/g, 'ı')
      .toLowerCase()
      .replace(/ı/g, 'i')
      .replace(/ş/g, 's')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/Ş/g, 's')
      .replace(/Ğ/g, 'g')
      .replace(/Ü/g, 'u')
      .replace(/Ö/g, 'o')
      .replace(/Ç/g, 'c');
  };

  // Benzersiz kullanıcı adlarını al
  const uniqueUsernames = useMemo(() => {
    if (!companyDocuments) return [];
    const usernames = companyDocuments
      .map(doc => doc.username)
      .filter((username): username is string => Boolean(username))
      .filter((username, index, arr) => arr.indexOf(username) === index);
    return usernames.sort();
  }, [companyDocuments]);

  // Filtrelenmiş ve sıralanmış dökümanlar
  const filteredDocuments = useMemo(() => {
    if (!companyDocuments) return null;

    let filtered = [...companyDocuments];

    // Arama filtresi
    if (searchTerm.trim()) {
      const normalizedSearchTerm = normalizeTurkish(searchTerm.trim());
      filtered = filtered.filter(doc => {
        const normalizedFileName = normalizeTurkish(doc.file_name_no_ext);
        const normalizedDocumentId = normalizeTurkish(doc.document_id);
        
        return normalizedFileName.startsWith(normalizedSearchTerm) || 
               normalizedDocumentId.startsWith(normalizedSearchTerm);
      });
    }

    // İsim filtresi
    if (nameFilter !== 'all') {
      filtered = filtered.filter(doc => doc.username === nameFilter);
    }

    // Tarih sıralaması
    filtered.sort((a, b) => {
      const dateA = a.current_date && a.current_time ? 
        parseInt(a.current_date + a.current_time) : 0;
      const dateB = b.current_date && b.current_time ? 
        parseInt(b.current_date + b.current_time) : 0;
      
      if (sortOrder === 'newest') {
        return dateB - dateA; // Yeni -> Eski
      } else {
        return dateA - dateB; // Eski -> Yeni
      }
    });

    return filtered;
  }, [companyDocuments, searchTerm, sortOrder, nameFilter]);
  return (
    <div className="w-full">
      {/* Sade Navigasyon */}
      <div className="mb-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <button
            onClick={onBackToOptions}
            className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
            </svg>
            <span className="text-sm font-medium">{selectedCompany?.name}</span>
          </button>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Çeviriler
            </div>
            <button
              onClick={onRefreshDocuments}
              disabled={isLoadingDocuments}
              className="flex items-center px-3 py-1.5 text-sm bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-800/40 text-purple-700 dark:text-purple-300 rounded-lg transition-colors disabled:opacity-50"
            >
              <svg className={`w-4 h-4 mr-1.5 ${isLoadingDocuments ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              Yenile
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{selectedCompany?.name} Çevirileri</h1>
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  Şirketinizin tüm çeviri dökümanlarını görüntüleyin ve yönetin
                </p>
              </div>
              <div className="hidden md:block">
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center">
                  <svg className="w-10 h-10 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoadingDocuments ? (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
                <svg className="w-8 h-8 text-purple-600 dark:text-purple-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Çeviriler Yükleniyor...
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Şirket çevirileri getiriliyor, lütfen bekleyin.
              </p>
            </div>
          </div>
        ) : documentsError ? (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-red-200/50 dark:border-red-700/50 p-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Hata Oluştu
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {documentsError}
              </p>
              <button
                onClick={onRefreshDocuments}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Tekrar Dene
              </button>
            </div>
          </div>
        ) : !companyDocuments || companyDocuments.length === 0 ? (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Henüz Çeviri Yok
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {selectedCompany?.name} için henüz çeviri dökümanı bulunmuyor. İlk çevirinizi yapmak için "Çeviri Yap" sekmesini kullanın.
              </p>
              <button
                onClick={onGoToTranslate}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
              >
                Çeviri Yapmaya Başla
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Toplam Döküman</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{companyDocuments.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Şirket</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white truncate">{selectedCompany?.name}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Son Güncelleme</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">Şimdi</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents Grid */}
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Çeviri Dökümanları</h2>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {filteredDocuments?.length || 0} / {companyDocuments?.length || 0} döküman
                    {(searchTerm || nameFilter !== 'all') && (
                      <span className="text-purple-600 dark:text-purple-400 ml-1">
                        (
                        {searchTerm && 'arama'}
                        {searchTerm && nameFilter !== 'all' && ', '}
                        {nameFilter !== 'all' && 'kullanıcı'}
                        {' filtreli'}
                        )
                      </span>
                    )}
                  </span>
                </div>
              </div>
              
              {/* Arama, Sıralama ve Filtreleme */}
              <div className="mb-6">
                <div className="flex gap-4">
                  {/* Arama Çubuğu - %50 */}
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Dosya adı veya ID ile ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Aramayı temizle"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Tarih Sıralaması - %25 */}
                  <div className="w-1/4">
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                      className="w-full py-2 px-3 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-1 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    >
                      <option value="newest">Yeni → Eski</option>
                      <option value="oldest">Eski → Yeni</option>
                    </select>
                  </div>

                  {/* İsim Filtresi - %25 */}
                  <div className="w-1/4">
                    <select
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      className="w-full py-2 px-3 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white focus:ring-1 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    >
                      <option value="all">Tüm Kullanıcılar</option>
                      {uniqueUsernames.map(username => (
                        <option key={username} value={username}>
                          {username}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDocuments && filteredDocuments.length > 0 ? (
                  filteredDocuments.map((doc) => (
                  <div
                    key={doc.document_id}
                    className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:shadow-lg hover:border-purple-400 dark:hover:border-purple-500 transition-all duration-200 cursor-pointer group"
                    onClick={() => onViewDocument(doc.document_id, doc.file_name_no_ext)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-800/40 transition-colors flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Completion Rate Pie Chart */}
                        {doc.checked_count !== undefined && doc.unchecked_count !== undefined && (
                          <div className="relative group/tooltip">
                            {/* Pie Chart Container */}
                            <div className="w-14 h-14 relative">
                              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                                {(() => {
                                  const checkedCount = parseInt(doc.checked_count || '0') || 0;
                                  const uncheckedCount = parseInt(doc.unchecked_count || '0') || 0;
                                  const total = checkedCount + uncheckedCount;
                                  const percentage = total > 0 ? (checkedCount / total) * 100 : 0;
                                  const circumference = 2 * Math.PI * 15.91549430918954;
                                  const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                                  
                                  return (
                                    <>
                                      {/* Background circle */}
                                      <circle
                                        cx="18"
                                        cy="18"
                                        r="15.91549430918954"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        className="text-gray-300 dark:text-gray-600"
                                      />
                                      {/* Progress circle */}
                                      <circle
                                        cx="18"
                                        cy="18"
                                        r="15.91549430918954"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeDasharray={strokeDasharray}
                                        className={percentage >= 70 ? 'text-green-600 dark:text-green-400' : percentage >= 40 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}
                                      />
                                      {/* Center percentage text */}
                                      <text
                                        x="18"
                                        y="18"
                                        textAnchor="middle"
                                        dy="0.3em"
                                        className="text-[9px] font-bold fill-gray-900 dark:fill-white"
                                        transform="rotate(90 18 18)"
                                      >
                                        {Math.round(percentage)}%
                                      </text>
                                    </>
                                  );
                                })()}
                              </svg>
                            </div>
                            
                            {/* Hover Tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-44 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] shadow-lg">
                              {/* Arrow */}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                              <div className="font-semibold mb-1.5 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                Kontrol Durumu
                              </div>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></div>
                                    <span className="text-[10px]">Kontrol Edildi:</span>
                                  </div>
                                  <span className="font-bold text-[10px]">{parseInt(doc.checked_count || '0') || 0}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-1.5"></div>
                                    <span className="text-[10px]">Kontrol Edilmedi:</span>
                                  </div>
                                  <span className="font-bold text-[10px]">{parseInt(doc.unchecked_count || '0') || 0}</span>
                                </div>
                                <div className="flex items-center justify-between pt-1 border-t border-gray-700 dark:border-gray-300 mt-1">
                                  <span className="text-[10px]">Toplam:</span>
                                  <span className="font-bold text-[10px]">{(parseInt(doc.checked_count || '0') || 0) + (parseInt(doc.unchecked_count || '0') || 0)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Completion Status Button - Sadece admin ve editor görebilir */}
                        {canToggleDocumentCompletion() && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleCompletion(doc.document_id, doc.is_completed || false);
                            }}
                            className={`p-1.5 rounded-md transition-colors ${
                              doc.is_completed 
                                ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20' 
                                : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-900/20'
                            }`}
                            title={doc.is_completed ? 'Tamamlandı - Tıkla değiştir' : 'Devam Ediyor - Tıkla değiştir'}
                          >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            {doc.is_completed ? (
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            ) : (
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            )}
                          </svg>
                        </button>
                        )}
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewDocument(doc.document_id, doc.file_name_no_ext);
                          }}
                          className="p-1.5 text-purple-600 hover:text-purple-800 hover:bg-purple-50 dark:text-purple-400 dark:hover:text-purple-300 dark:hover:bg-purple-900/20 rounded-md transition-colors"
                          title="Dökümanı Görüntüle"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                          </svg>
                        </button>
                        {/* Döküman Sil Butonu - Sadece admin görebilir */}
                        {canDeleteDocuments() && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteDocument(doc.document_id);
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                            title="Dökümanı Sil"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="font-semibold text-base text-gray-900 dark:text-white truncate" title={doc.file_name_no_ext}>
                        {doc.file_name_no_ext}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        ID: {doc.document_id.substring(0, 8)}...
                      </p>
                      <div className="flex flex-col gap-1.5">
                        {doc.username && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Yaratan:</span>
                            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                              @{doc.username}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Atanan:</span>
                          {doc.assigned_usernames ? (
                            (() => {
                              const usernames = doc.assigned_usernames.split(',').map(u => u.trim());
                              const firstUser = usernames[0];
                              const remainingCount = usernames.length - 1;
                              
                              return (
                                <>
                                  {/* Tek kullanıcı varsa hover'lı, çok kullanıcı varsa hover'sız */}
                                  {remainingCount === 0 ? (
                                    <div className="relative group">
                                      <span className="text-xs text-gray-700 dark:text-gray-300 font-medium bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded cursor-help">
                                        @{firstUser}
                                      </span>
                                      {/* Hover Tooltip - Tek kullanıcı */}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] shadow-lg">
                                        <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                          Atanan Kullanıcı
                                        </div>
                                        <div className="text-[10px] text-center">
                                          @{firstUser}
                                        </div>
                                        {/* Arrow */}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-700 dark:text-gray-300 font-medium bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                                      @{firstUser}
                                    </span>
                                  )}
                                  
                                  {/* +N badge - sadece birden fazla kullanıcı varsa - TÜM kullanıcıları gösterir */}
                                  {remainingCount > 0 && (
                                    <div className="relative group">
                                      <span className="text-xs text-gray-700 dark:text-gray-300 font-medium bg-purple-100 dark:bg-purple-800/30 px-2 py-0.5 rounded cursor-help">
                                        +{remainingCount}
                                      </span>
                                      {/* Hover Tooltip - TÜM kullanıcılar */}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] shadow-lg">
                                        <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                          Tüm Atanan Kullanıcılar
                                        </div>
                                        <div className="space-y-1">
                                          {usernames.map((username, idx) => (
                                            <div key={idx} className="text-[10px]">
                                              @{username}
                                            </div>
                                          ))}
                                        </div>
                                        {/* Arrow */}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()
                          ) : (
                            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">-</span>
                          )}
                        </div>
                      </div>
                      {doc.current_date && doc.current_time && (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            {formatDateTime(doc.current_date, doc.current_time)}
                          </span>
                        </p>
                      )}
                      {doc.source_language && doc.destination_language && (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
                            </svg>
                            {getLanguageName(doc.source_language)} → {getLanguageName(doc.destination_language)}
                          </span>
                        </p>
                      )}
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
                          doc.is_completed 
                            ? 'bg-green-500 text-white dark:bg-green-600' 
                            : 'bg-orange-500 text-white dark:bg-orange-600'
                        }`}>
                          {doc.is_completed ? (
                            <>
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Tamamlandı
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              Devam Ediyor
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-600">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDocument(doc.document_id, doc.file_name_no_ext);
                        }}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                        Görüntüle
                      </button>
                    </div>
                  </div>
                  ))
                ) : searchTerm ? (
                  <div className="col-span-full text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                      <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Arama Sonucu Bulunamadı
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      "<span className="font-medium">{searchTerm}</span>" araması için eşleşen döküman bulunamadı.
                    </p>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                      Aramayı Temizle
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDocuments;
